import { yupResolver } from '@hookform/resolvers/yup';
import hasUmlauts from '@medflex/utils/hasUmlauts';
import { isMobilePhoneNumber, EMAIL_REGEX } from '@medflex/shared-utils';
import * as PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Textarea from 'react-textarea-autosize';
import * as yup from 'yup';
import Button from '../Button';
import InputCheckbox from '../InputCheckbox';
import InputField from '../InputField';
import PhoneNumber from '../PhoneNumber';
import FormErrorMessage from '../FormErrorMessage';
import SwitchButton, { Switch } from '../SwitchButton';
import SMSFeatureNotice from '../SMSFeatureNotice/SMSFeatureNotice';
import Link from '../Link';

const InvitePhysicianForm = ({ onSubmit, isComposing, sender, canSendSms, smsUpgradeUrl }) => {
  const { t } = useTranslation('components');
  const [isEmailMode, setIsEmailMode] = useState(true);

  const emailSchema = yup.object().shape({
    email: yup
      .string()
      .required('')
      .test('', t('global:validation.messages.emailUmlauts'), value => value && !hasUmlauts(value))
      .matches(EMAIL_REGEX, t('global:validation.messages.email'))
      .label(t('InvitePhysicianForm.inputs.email.label')),
    text: yup.string().required(t('global:validation.messages.required')),
  });
  const mobileNumberSchema = yup.object().shape({
    mobileNumber: yup
      .string()
      .required('')
      .test('has valid format', t('global:validation.messages.mobileNumber'), value =>
        isMobilePhoneNumber(value),
      )
      .label(t('InvitePhysicianForm.inputs.mobile_number.label')),
  });

  const {
    control,
    errors,
    formState,
    handleSubmit: wrapSubmit,
    register: registerFormElement,
    reset,
    setValue,
    watch,
  } = useForm({
    resolver: yupResolver(isEmailMode ? emailSchema : mobileNumberSchema),
    mode: 'onChange',
  });
  const { isValid } = formState;
  const isCheckboxChecked = watch('suggestion');

  useEffect(() => {
    if (isCheckboxChecked) {
      setValue(
        'text',
        `Liebe Kollegin, lieber Kollege,\r\rich möchte gerne in Zukunft über den praktischen & zertifizierten medflex-Messenger Nachrichten und Dateien mit Ihnen austauschen und evtl. Videokonsile durchführen. Der Messenger ist übrigens kostenfrei und unverbindlich.\r\rIch freue mich darauf, dass Sie meine Einladung zu medflex annehmen.\r\rFreundliche Grüße, ${sender}`,
        { shouldValidate: true },
      );
    } else {
      setValue('text', '', { shouldValidate: true });
    }
  }, [isCheckboxChecked]);

  const isSubmitDisabled = !isValid || isComposing;

  const handleSwitch = ({ value }) => {
    setIsEmailMode(value === 'email');
  };

  const handleSubmit = formData => {
    onSubmit({
      ...(isEmailMode
        ? { email: formData.email, text: formData.text }
        : { mobileNumber: formData.mobileNumber }),
    });
    reset();
  };

  return (
    <form
      onSubmit={wrapSubmit(handleSubmit)}
      data-component="PhysicianInvitePhysicianModule"
      className="bg-slate-100 rounded-sm p-4"
    >
      <Switch onChange={handleSwitch} className="mb-6">
        <SwitchButton value="email" checked={isEmailMode}>
          {t('InvitePhysicianForm.typeSwitch.email')}
        </SwitchButton>
        <div className="ml-2" />
        <SwitchButton value="sms" checked={!isEmailMode}>
          {t('InvitePhysicianForm.typeSwitch.phone')}
        </SwitchButton>
      </Switch>
      {!canSendSms && !isEmailMode ? (
        <SMSFeatureNotice
          title={t('InvitePhysicianForm.smsNotice.title')}
          description={t('InvitePhysicianForm.smsNotice.description')}
        >
          <Link
            href={smsUpgradeUrl}
            target="_blank"
            rel="noreferrer"
            kind="button"
            className="text-lg"
          >
            {t('InvitePhysicianForm.smsNotice.cta')}
          </Link>
        </SMSFeatureNotice>
      ) : (
        <>
          <div className="mb-6">
            {/* Both input fields have to be rendered. otherwise react-hook-form gets confused */}
            <div hidden={!isEmailMode} className="space-y-1 mb-6">
              <label className=" font-semibold" htmlFor="email">
                {t('InvitePhysicianForm.inputs.email.label')}
              </label>
              <InputField
                ref={registerFormElement}
                defaultValue=""
                className="max-w-96"
                id="email"
                name="email"
                type="email"
                readOnly={isComposing}
                placeholder={t('InvitePhysicianForm.inputs.email.placeholder')}
              />
              {errors.email && <FormErrorMessage message={errors.email.message} />}
            </div>
            <div className={`space-y-1 ${isEmailMode ? 'hidden' : ''}`}>
              <label className="mb-1 font-semibold" htmlFor="email">
                {t('InvitePhysicianForm.inputs.mobile_number.label')}
              </label>
              <PhoneNumber
                name="mobileNumber"
                control={control}
                placeholder={t('InvitePhysicianForm.inputs.mobile_number.placeholder')}
              />
              {errors.mobileNumber && <FormErrorMessage message={errors.mobileNumber.message} />}
            </div>
          </div>
          <div className={`mb-6 ${!isEmailMode ? 'hidden' : ''}`}>
            <label htmlFor="text">
              <span className="font-semibold mb-1 block">
                {t('InvitePhysicianForm.inputs.text.label')}
              </span>
            </label>

            <Controller
              control={control}
              defaultValue=""
              name="text"
              render={({ onChange, value, name, ref }) => (
                <Textarea
                  className="p-3 mb-2 resize-none border border-slate-500 focus:border-blue-500 rounded outline-none placeholder-slate-500 leading-tight w-full min-h-28"
                  id="personalNote"
                  ref={ref}
                  maxLength="2000"
                  minRows={3}
                  name={name}
                  onChange={val => onChange(val)}
                  placeholder={t('InvitePhysicianForm.inputs.text.placeholder')}
                  value={value}
                />
              )}
              rules={{ required: true }}
            />

            <div className="inline-flex items-center mb-8" htmlFor="terms">
              <InputCheckbox
                id="suggestion"
                name="suggestion"
                className="flex-none h-6 w-6"
                ref={registerFormElement}
              />
              <label
                htmlFor="suggestion"
                className="ml-3 cursor-pointer flex-1 w-full text-sm rounded overflow-y-auto overflow-x-hidden"
              >
                {t('InvitePhysicianForm.inputs.checkbox')}
              </label>
            </div>
          </div>
          <div className="flex justify-end">
            <Button isDisabled={isSubmitDisabled} isLoading={isComposing} type="submit">
              {t('InvitePhysicianForm.buttons.send')}
            </Button>
          </div>
        </>
      )}
    </form>
  );
};

InvitePhysicianForm.defaultProps = {
  onSubmit: () => {},
  isComposing: false,
  smsUpgradeUrl: '',
};

InvitePhysicianForm.propTypes = {
  isComposing: PropTypes.bool,
  onSubmit: PropTypes.func,
  sender: PropTypes.string.isRequired,
  canSendSms: PropTypes.bool.isRequired,
  smsUpgradeUrl: PropTypes.string,
};

export default InvitePhysicianForm;
